import React, { memo, useEffect, useState } from "react";
import styled from "styled-components";

import { ExclamationMarkIcon } from "@8x8/oxygen-icon";
import Input from "@8x8/oxygen-input";
import Select from "@8x8/oxygen-select";
import Tag from "@8x8/oxygen-tag";
import Tooltip, { orientation, showOn } from "@8x8/oxygen-tooltip";

import LabeledDateSelector from "@/react/components/LabeledDateSelector";
import {
  StyledCheckIcon,
  StyledCloseCircleIcon,
} from "@/react/components/styled/StyledIcons";
import StyledToggleButton from "@/react/components/styled/StyledToggleButton";
import { CopyButton } from "@/react/components/CopyButton";
import {
  formatTimestamp,
  timestampFormatter,
  TIMESTAMP,
  UTC_DATE_TIME_FORMAT,
} from "@/react/datetime";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";

import HasErrorSpan from "./HasErrorSpan";
import StyledValidationWrapper from "./StyledValidationWrapper";
import ValidationWrapper from "./ValidationWrapper";

import useEditableCell from "../useEditableCell";
import { cellErrors } from "../useTableMutations";
import { isNil } from "lodash";

const AlignedHeader = styled.h6`
  margin: 0;
`;

export const StringCell = memo((cell) => {
  const value = cell.getValue();
  const customCell = cell.column.columnDef.customCell;
  const errors = cellErrors(cell);
  const disableCopy = cell.column.columnDef.disableCopy === true;

  const hasError = errors?.length > 0;
  const hasNoValue = isNil(value) || value === "";
  return (
    <>
      <StyledValidationWrapper
        centered={hasNoValue} /* So that the error marker is centered */
        errors={errors}
      >
        {hasError && hasNoValue ? (
          <ExclamationMarkIcon color={"red"} />
        ) : (
          <HasErrorSpan hasError={hasError} title={value}>
            {customCell ? customCell(cell) : value}
          </HasErrorSpan>
        )}
      </StyledValidationWrapper>
      {!hasNoValue && !disableCopy && <CopyButton value={value} />}
    </>
  );
});

export const IdCell = memo((cell) => {
  const errors = cellErrors(cell);
  const hasErrors = errors?.length > 0;
  const hasNoValue = !Boolean(cell.getValue());

  return (
    <>
      {hasErrors && hasNoValue ? (
        <StyledValidationWrapper errors={errors} centered>
          <ExclamationMarkIcon color={"red"} />
        </StyledValidationWrapper>
      ) : (
        <StringCell {...cell}></StringCell>
      )}
    </>
  );
});

export const EditableStringCell = (cell) => {
  const { value, onChange, onBlur } = useEditableCell(cell);
  const errors = cellErrors(cell);
  return (
    <ValidationWrapper errors={errors}>
      <Input
        size={"small"}
        type="text"
        hasError={errors?.length > 0}
        placeholder={`Type value...`}
        value={String(value || "")}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    </ValidationWrapper>
  );
};

export const BooleanCell = memo((cell) => (
  <span>
    {cell.getValue() ? <StyledCheckIcon /> : <StyledCloseCircleIcon />}
  </span>
));

export const EditableBooleanCell = (cell) => {
  const { value, onChange, onBlur } = useEditableCell(cell);
  const errors = cellErrors(cell);
  let theme = {};
  // `hasError` does not seem to exist on ToggleButton, so modifying the theme directly
  if (errors?.length > 0) {
    theme.backgroundColor = "red";
    theme.backgroundColorDisabled = "red";
    theme.backgroundColorSelected = "red";
  }
  return (
    <ValidationWrapper errors={errors}>
      <StyledToggleButton
        isChecked={Boolean(value)}
        onChange={() => onChange(!Boolean(value))}
        onBlur={onBlur}
        theme={theme}
      />
    </ValidationWrapper>
  );
};

export const EditableNumberCell = (cell) => {
  const { value, onChange, onBlur } = useEditableCell(cell);
  const errors = cellErrors(cell);
  return (
    <ValidationWrapper errors={errors}>
      <Input
        size={"small"}
        type="number"
        hasError={errors?.length > 0}
        value={Number(value)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    </ValidationWrapper>
  );
};
export const SelectCell = memo(({ getValue, row, column }) => {
  const value =
    (column.columnDef.options && column.columnDef.options[getValue()]) ||
    getValue();
  const errors = cellErrors({ row, column });
  return (
    <ValidationWrapper errors={errors}>
      <AlignedHeader>
        <Tag hasError={errors?.length > 0}>{value}</Tag>
      </AlignedHeader>
    </ValidationWrapper>
  );
});

export const EditableSelectCell = (cell) => {
  const { value, onChange, onBlur } = useEditableCell(cell);
  const errors = cellErrors(cell);

  const options = Object.keys(cell.column.columnDef.options).map((key) => ({
    value: key,
    label: cell.column.columnDef.options[key],
  }));
  const selectValue = options.find((option) => option.value === value);

  const onChangeSelect = (opt) => {
    onChange(opt?.value);
  };

  return (
    <FullWidthDiv>
      <ValidationWrapper errors={errors}>
        <Select
          size={"small"}
          value={selectValue}
          options={options}
          onChange={onChangeSelect}
          onBlur={onBlur}
          hasError={errors.length > 0}
          isClearable={false}
          isSearchable={true}
        />
      </ValidationWrapper>
    </FullWidthDiv>
  );
};

// Note: For DateTimeCell and EditableDateTimeCell, it is expected that the cell value is a unix timestamp
// When using this type of cell, you should specify in the column definition:
// - Whether the timestamp is expressed in seconds or milliseconds (columnDef.timestampUnit).
// - The formatter to use to convert the timestamp to display format (columnDef.timestampFormatter)
// The default is to use milliseconds and format in UTC time.
const defaultUnit = TIMESTAMP.UNITS.MILLISECONDS;
const defaultFormatter = timestampFormatter(
  defaultUnit,
  TIMESTAMP.ZONE.UTC,
  UTC_DATE_TIME_FORMAT
);
const defaultHasTooltip = true;

export const DateTimeCell = memo((cell) => {
  const dateTimeStamp = cell.getValue();
  const formatter =
    cell.column.columnDef.timestampFormatter ?? defaultFormatter;
  const timestampUTC = formatTimestamp({
    ts: dateTimeStamp,
    units: cell.column.columnDef.timestampUnit ?? defaultUnit,
    zone: TIMESTAMP.ZONE.UTC,
    format: UTC_DATE_TIME_FORMAT,
  });
  const hasTooltip = cell.column.columnDef.tooltip ?? defaultHasTooltip;
  const value = formatter(dateTimeStamp);
  const errors = cellErrors(cell);
  const disableCopy = cell.column.columnDef.disableCopy === true;

  return (
    <>
      <StyledValidationWrapper errors={errors}>
        <HasErrorSpan hasError={errors?.length > 0} title={value}>
          {/* Avoid double tooltips */}
          {hasTooltip && errors?.length === 0 ? (
            <Tooltip
              orientation={orientation.top}
              showOn={showOn.hover}
              title={timestampUTC}
            >
              {value}
            </Tooltip>
          ) : (
            <>{value}</>
          )}
        </HasErrorSpan>
      </StyledValidationWrapper>
      {value && !disableCopy && <CopyButton value={value} />}
    </>
  );
});

export const EditableDateTimeCell = (cell) => {
  const { value, onChange, onBlur } = useEditableCell(cell);
  const timestampUnit = cell.column.columnDef.timestampUnit ?? defaultUnit;
  // The Date constructor expects the timestamp to be in milliseconds
  const [dateTimeValue, setDateTimeValue] = useState(
    new Date(timestampUnit === TIMESTAMP.UNITS.SECONDS ? value * 1000 : value)
  );
  const errors = cellErrors(cell);

  useEffect(() => {
    onChange(
      timestampUnit === TIMESTAMP.UNITS.SECONDS
        ? dateTimeValue.valueOf() / 1000
        : dateTimeValue.valueOf()
    );
  }, [dateTimeValue]);

  return (
    <ValidationWrapper errors={errors}>
      {/* TODO: Fix how this potentially expands the cell too much */}
      <LabeledDateSelector
        size={"small"}
        value={dateTimeValue}
        setValue={setDateTimeValue}
        closeOnDateChange
        singleDate
        onBlur={onBlur}
      />
    </ValidationWrapper>
  );
};
